import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import {
  ButtonSize,
  LoaderText,
  PrimaryCTAButton,
  SecondaryCTAButton,
  TransparentCTAButton,
} from "../../shared/CTAButtonComponents";
import { Input, Label } from "../../shared/InputField";
import {
  Error,
  HStack,
  PlatformEnum,
  PlatformUrlMapping,
  VStack,
} from "../../shared/utils";
import { useFormik } from "formik";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { GoogleLoginButton } from "./GoogleAuthPage";
import {
  EmailOTPType,
  useForgotPassword,
  useGenerateOTP,
  useSignIn,
} from "../../queries/auth";
import SessionStorageService, {
  SessionStorageKeys,
} from "../../utils/sessionStorage";
import { AuthStrategyType, UserType } from "../../types/auth";
import { useAuthStore } from "../../store/useAuthStore";
import { decodeAuthToken } from "../../utils/auth";
import { handleClickForTracking } from "../../analytics";
import { AnalyticsConstant } from "../../AnalyticsContant";
import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { TriangleAlert } from "lucide-react";
import { trackEvent } from "../../utils/AmpAnalytics";
import { MicrosoftLoginButton } from "./MIcrosoftAuthPage";
import { useFeatureFlag } from "../../FeatureFlags";
import { startAuthentication } from "@simplewebauthn/browser";
import {
  passkeyAuthenticateBegin,
  passkeyAuthenticateFinish,
} from "../../api/auth";

/** Convert backend errors to safe, user-facing messages. */
function safeErrorMessage(err: any, fallback: string): string {
  const raw: string =
    err?.response?.data?.message ?? err?.message ?? "";
  if (
    !raw ||
    raw.includes("at ") ||
    raw.includes("Error:") ||
    raw.length > 200
  ) {
    return fallback;
  }
  return raw;
}

type InitialValuesType = {
  email: string;
};

type InitialPasswordType = {
  password: string;
  isPassword: boolean;
  otp: string;
};

export const EmailComponent = (props: any) => {
  const initialValues: InitialValuesType = {
    email: props.email || "",
  };
  const emailSchema = Yup.object().shape({
    email: Yup.string()
      .email("Wrong email format")
      .required("Please Enter Email Address")
      .label("Email"),
  });

  const formik = useFormik<InitialValuesType>({
    initialValues,
    validationSchema: emailSchema,
    validateOnChange: true,
    onSubmit: (values: InitialValuesType) => {
      if (values.email !== undefined && values.email !== "") {
        props.setEmail(values.email);
      }
    },
  });

  const hasEmail = (formik.values.email ?? "").trim().length > 0;
  const [isEmailFocused, setIsEmailFocused] = useState(false);

  return (
    <form className="" onSubmit={formik.handleSubmit}>
      {!props.isMarketplace && (
        <p className="text-xs my-8">
          {
            "Sign in to explore the ESOP, Cap Table of your company and your Portfolio."
          }
        </p>
      )}
      <div className="flex justify-center text-center my-4">
        <MicrosoftLoginButton />
      </div>
      <div className={`${!props.isMarketplace ? "mb-4" : "mb-4"} `}>
        <SigninGoogleComponent />
      </div>
      <div className="flex items-center justify-center my-4">
        <div className="flex-grow border-t border-gray-300"></div>
        <span className="mx-4 text-gray-500">or</span>
        <div className="flex-grow border-t border-gray-300"></div>
      </div>

      <div className="">
        <Input
          id="email"
          className="md:w-[400px]"
          type="email"
          autoComplete="email"
          placeholder="Email Address"
          {...formik.getFieldProps("email")}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
            setIsEmailFocused(true);
            formik.getFieldProps("email").onBlur(e);
          }}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            setIsEmailFocused(false);
            formik.handleBlur(e);
          }}
        />
        {formik.touched?.email && formik.errors?.email && (
          <Error
            className="text-left md:w-[400px] mx-auto"
            text={formik.errors.email}
          />
        )}
      </div>

      <div className="">
        {props.isMarketplace ? (
          <SecondaryCTAButton
            event-name="Email Continue"
            className={`${props.isMarketplace ? "w-full mt-4" : "w-32 mt-6"} text-center px-auto transition-all duration-300 ${
              hasEmail
                ? "!bg-primary !text-white !border-primary hover:!bg-primary/80"
                : isEmailFocused
                ? "!border-primary"
                : ""
            }`}
            buttonSize={ButtonSize.MEDIUM}
            loading={props.loading}
            type="submit"
          >
            <p className="w-full text-sm leading-0"> Continue</p>
          </SecondaryCTAButton>
        ) : (
          <PrimaryCTAButton
            event-name="Email Continue"
            className={`w-32 mt-6 text-center px-auto`}
            buttonSize={ButtonSize.MEDIUM}
            loading={props.loading}
            type="submit"
          >
            <p className="w-full text-sm leading-0"> Continue</p>
          </PrimaryCTAButton>
        )}
      </div>

      {props.isPasskeySupported && props.hasPasskeyRegistered && (
        <>
          <div className="flex items-center justify-center my-3">
            <div className="flex-grow border-t border-gray-300"></div>
            <span className="mx-4 text-gray-500 text-xs">or</span>
            <div className="flex-grow border-t border-gray-300"></div>
          </div>
          <TransparentCTAButton
            className={`${props.isMarketplace ? "w-full" : "w-full md:w-[400px]"} text-center`}
            buttonSize={ButtonSize.MEDIUM}
            loading={props.passkeyLoading}
            type="button"
            onClick={() => props.onUsernamelessPasskeyLogin()}
          >
            <p className="w-full text-sm">Sign in with Passkey</p>
          </TransparentCTAButton>
        </>
      )}
    </form>
  );
};

export const SigninGoogleComponent = () => {
  return (
    <>
      <div className="flex justify-center text-center">
        {/* <TransparentCTAButton
            image={"/media/google.png"}
            onClick={() => {
              handleGoogleSignin();
            }}
            className="max-w-full w-[90%] sm:w-[300px]"
          >
            Sign in with Google
          </TransparentCTAButton> */}
        <GoogleLoginButton />
      </div>
      {/* <div className="text-center mt-4">
        <TransparentCTAButton
          image={"/media/apple.png"}
          disabledImage={"/media/gray-apple.png"}
          onClick={() => {}}
          disabled={true}
          className="w-[275px]"
        >
          Sign in with Apple
        </TransparentCTAButton>
      </div> */}
    </>
  );
};

export const AccountSelectionComponent = (props: any) => {
  const [selectedTab, setSelectedTab] = useState<UserType>(props.selectedRole);

  useEffect(() => {
    props.setSelectedRole(selectedTab);
  }, [selectedTab]);
  return (
    <>
      <div className="mb-6">
        <p className="text-lg">Multiple Accounts Discovered</p>
        <p className="text-gray-400 text-sm">
          Select account you would like to proceed with
        </p>
      </div>
      <VStack className="flex-col justify-center gap-5">
        <div
          className="text-center"
          onClick={() => {
            setSelectedTab(UserType.EQUION);
          }}
        >
          <div
            className={`w-full md:w-[400px] shadow-sm lg:p-3 border cursor-pointer rounded-md mx-auto ${
              selectedTab === UserType.EQUION ? "border-primary border-2" : ""
            }`}
          >
            {/* <div className="grid grid-cols-3"> */}
            <HStack className="p-2">
              <div className="flex justify-center items-center mx-2">
                <img
                  className="w-12 h-12 mr-2"
                  src={"/media/Admin_account.svg"}
                  alt={"button-image"}
                />
              </div>
              <div className="col-span-2 text-left ms-3">
                <h2 className="">Admin Account</h2>
                <p className="text-xs text-gray-400">
                  Login as admin to manage employees under your organization
                </p>
              </div>
            </HStack>
            {/* </div> */}
          </div>
        </div>
        <div
          className="text-center"
          onClick={() => {
            setSelectedTab(UserType.EMPLOYEE);
          }}
        >
          <div
            className={`w-full md:w-[400px] shadow-sm lg:p-3 border cursor-pointer rounded-md mx-auto ${
              selectedTab === UserType.EMPLOYEE ? "border-primary border-2" : ""
            }`}
          >
            {/* <div className="grid grid-cols-3 p-2"> */}
            <HStack className="p-2">
              <div className="flex justify-center items-center mx-2">
                <img
                  className="w-12 h-12 mr-2"
                  src={"/media/Employee_account.svg"}
                  alt={"button-image"}
                />
              </div>
              <div className="col-span-2 text-left ms-3">
                <h2 className="">Employee Account</h2>
                <p className="text-xs text-gray-400">
                  Login as Employee to manage your accounts and portfolios.
                </p>
              </div>
              {/* </div> */}
            </HStack>
          </div>
        </div>
      </VStack>
      <div className="mt-6 mb-6">
        <PrimaryCTAButton
          event-name="Password"
          className="w-auto text-center px-auto "
          buttonSize={ButtonSize.MEDIUM}
          type="submit"
          loading={props.loading}
          onClick={() => {
            props.onSubmit();
          }}
        >
          <p className="w-full text-sm px-2 leading-0">
            {" "}
            {`Sign in as ${
              selectedTab === UserType.EMPLOYEE ? "Employee" : "Admin"
            }`}
          </p>
        </PrimaryCTAButton>
      </div>
    </>
  );
};

export const PasswordComponent = (props: any) => {
  const [isPassword, setIsPassword] = useState<boolean>(props.isPassword);
  const [isForgotPasswordLoading, setIsForgotPasswordLoading] =
    useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [transactionId, setTransactionId] = useState<string>();
  const [showPassword, setShowPassword] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  const isPasskeySupported =
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential === "function";
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const sessionService = new SessionStorageService();
  const location = useLocation();
  const isMarketplace = useFeatureFlag("allowUserRegistration");
  const isPasskeyFeatureEnabled = useFeatureFlag("enablePasskey");

  const queryParams = new URLSearchParams(location.search);
  let platform: PlatformEnum | null | undefined = queryParams.get(
    "platform",
  ) as PlatformEnum;
  let redirect = platform ? PlatformUrlMapping[platform] : undefined;
  const email = props.email || sessionService.getItem(SessionStorageKeys.EMAIL);
  useEffect(() => {
    let timer: any;
    if (timeLeft > 0) {
      timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    } else {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [timeLeft]);

  const { mutate: signIn } = useSignIn();
  const { mutate: generateOTP } = useGenerateOTP();
  const { mutate: forgotPassword } = useForgotPassword();

  const handleResend = () => {
    generateOTP(
      { email: email, userType: props.selectedRole } as EmailOTPType,
      {
        onSuccess: (data) => {
          setTimeLeft(data.data.interval);
          setTransactionId(data.data.transactionId);
          setCanResend(false);
          sessionService.setItem(
            SessionStorageKeys.TRANSACTION_ID,
            data.data.transactionId,
          );
          trackEvent("Generating OTP", {
            email: data?.data?.user?.emailId || email,
            name: data?.data?.user?.fullName || email,
          });
          toast("OTP has been sent to your Email", {
            type: "success",
            autoClose: 3000,
          });
        },
        onError: (error: any) => {
          toast(
            error?.response?.data?.message || "Please try after some time",
            {
              type: "error",
              autoClose: 3000,
            },
          );
        },
      },
    );
  };

  useEffect(() => {
    if (props.isOTP === true && props.isPassword === false) {
      formik.setFieldValue("isPassword", false);
      handleResend();
    }
  }, [props.isOTP, props.isPassword]);

  const handlePasskeyLogin = async () => {
    setPasskeyLoading(true);
    try {
      const beginRes = await passkeyAuthenticateBegin({
        email,
        userType: props.selectedRole,
      });
      const { transactionId: passkeyTxnId, options } = beginRes.data;

      const authResponse = await startAuthentication({ optionsJSON: options });

      const finishRes = await passkeyAuthenticateFinish({
        email,
        userType: props.selectedRole,
        authStrategyType: "PASSKEY",
        passkeyTransactionId: passkeyTxnId,
        passkeyAuthenticationResponse: authResponse,
      });

      const accessToken = finishRes.data;
      const decodedJwt = decodeAuthToken(accessToken);
      sessionService.clear();
      toast("Signin Successful", { type: "success", autoClose: 2000 });
      trackEvent("Logging In with Passkey", {
        email: decodedJwt.emailId || email,
        name: decodedJwt.fullName || email,
      });
      if (redirect) {
        window.location.href = redirect;
      } else if (props.lastUsedProduct) {
        window.location.href =
          PlatformUrlMapping[props.lastUsedProduct as PlatformEnum] ||
          "/landing/dashboard";
      } else {
        setAuth({
          accessToken,
          isAuthenticated: true,
          email: decodedJwt.emailId,
          name: decodedJwt.fullName,
          id: decodedJwt.id,
        });
        navigate("/landing/dashboard");
      }
    } catch (err: any) {
      const msg = safeErrorMessage(err, "Passkey login failed");
      if (
        msg.toLowerCase().includes("cancel") ||
        err?.name === "NotAllowedError"
      ) {
        toast("Cancelled. Please try again.", { type: "info", autoClose: 2000 });
      } else if (
        msg.toLowerCase().includes("not found") ||
        msg.toLowerCase().includes("invalid") ||
        msg.toLowerCase().includes("credential")
      ) {
        toast(
          "This passkey is no longer valid. Please remove it from your password manager and try a different sign-in method.",
          { type: "error", autoClose: 5000 }
        );
      } else {
        toast(msg, { type: "error", autoClose: 2000 });
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setIsForgotPasswordLoading(true);
    forgotPassword(
      { email: email, userType: props.selectedRole } as EmailOTPType,
      {
        onSuccess: (data: any) => {
          setIsForgotPasswordLoading(false);
          trackEvent("Generating OTP to reset password", {
            email: email,
            name: email,
          });
          toast(
            data?.message ||
              "A Forgot password OTP has been sent to your Email",
            {
              type: "success",
              autoClose: 3000,
            },
          );
          const transactionData = {
            transactionId: data?.data,
            selectedRole: props.selectedRole,
          };
          navigate("/resetpassword", { state: transactionData });
        },
        onError: (error: any) => {
          setIsForgotPasswordLoading(false);
          toast(
            error?.response?.data?.message ||
              "Error in generating OTP, Please try after some time",
            {
              type: "error",
              autoClose: 3000,
            },
          );
        },
      },
    );
  };

  const initialPasswordValues: InitialPasswordType = {
    password: "",
    isPassword: isPassword === true,
    otp: "",
  };

  const emailSchema = Yup.object().shape({
    otp: Yup.string().when(["isPassword"], {
      is: (isPassword: boolean) => isPassword === false,
      then: Yup.string().required().label("OTP"),
      otherwise: Yup.string().notRequired(),
    }),
    password: Yup.string().when(["isPassword"], {
      is: (isPassword: boolean) => isPassword === true,
      then: Yup.string().required().label("Password"),
      otherwise: Yup.string().notRequired(),
    }),
  });

  const formik = useFormik<InitialPasswordType>({
    initialValues: initialPasswordValues,
    validationSchema: emailSchema,
    // validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values: InitialPasswordType) => {
      setLoading(true);
      // handleClickForTracking(AnalyticsConstant.login_button);
      signIn(
        {
          email: email,
          password: values.isPassword ? values.password : values.otp,
          authStrategyType: values.isPassword
            ? AuthStrategyType.PASSWORD
            : AuthStrategyType.EMAIL_OTP,
          userType: props.selectedRole,
          redirect: props.redirect,
          otp: values.otp.toString(),
          transactionId: transactionId,
        },
        {
          onSuccess: (data) => {
            setLoading(false);
            if (data.data) {
              const accessToken = data.data;
              const decodedJwt = decodeAuthToken(accessToken);
              sessionService.clear();
              toast("Signin Successful", {
                type: "success",
                autoClose: 2000,
              });
              trackEvent("Logging In", {
                email: decodedJwt.emailId || email,
                name: decodedJwt.fullName || email,
              });

              if (isMarketplace) {
                const marketplaceLink = process.env.REACT_APP_MARKETPLACE_URL;
                window.location.replace(marketplaceLink || "/login");
              } else if (redirect) {
                window.location.href = redirect;
              } else if (
                props.lastUsedProduct !== undefined &&
                props.lastUsedProduct &&
                redirect === undefined
              ) {
                const lastUsedProductLink =
                  PlatformUrlMapping[props.lastUsedProduct as PlatformEnum];
                window.location.href =
                  lastUsedProductLink || "/landing/dashboard";
              } else {
                setAuth({
                  accessToken,
                  isAuthenticated: true,
                  email: decodedJwt.emailId,
                  name: decodedJwt.fullName,
                  id: decodedJwt.id,
                });
                navigate("/landing/dashboard");
              }
            }
          },
          onError: (err: any) => {
            setLoading(false);
            toast(safeErrorMessage(err, "Something went wrong, try again later"), {
              type: "error",
              autoClose: 2000,
            });
          },
        },
      );
    },
  });

  const isPasskeyOnly =
    !props.isPassword &&
    !props.isOTP &&
    isPasskeyFeatureEnabled &&
    isPasskeySupported &&
    props.authStrategies?.includes(AuthStrategyType.PASSKEY) &&
    props.currentUserType?.hasPasskeyRegistered;

  return (
    <form className="" onSubmit={formik.handleSubmit}>
      <p className="text-center w-full md:w-[400px] mb-4 mt-6">
        {isPasskeyOnly
          ? "Sign in with your passkey"
          : isPassword
          ? "Enter Password to Sign in"
          : "We've sent a verification code to your email"}
      </p>

      <div className="bg-gray-100 rounded-md p-1 md:p-2 w-full md:w-[400px] mx-auto">
        <HStack className="justify-between">
          <div></div>
          <HStack>
            <img
              className="w-7 h-7 mr-2"
              src={"/media/user-circle.svg"}
              alt={"button-image"}
            />
            <p className="truncate">{email}</p>
          </HStack>
          <div className="flex items-center cursor-pointer">
            <Icon
              icon="zondicons:close-solid"
              className="text-gray-400 placeholderLoader rounded-lg mr-2 hover:bg-gray-300"
              height={16}
              onClick={() => props.onClearEmail()}
            />
          </div>
        </HStack>
      </div>

      <div className="mt-4">
        {isPasskeyOnly ? null : isPassword ? (
          <>
            <div className="mx-auto w-full md:w-[400px]">
              <div className="relative">
                <Input
                  className="w-full pr-10"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  {...formik.getFieldProps("password")}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 mt-2 right-3 flex items-center text-gray-500"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {!showPassword ? (
                    <Icon
                      icon="garden:eye-hide-fill-12"
                      className="text-gray-400 placeholderLoader rounded-lg"
                      height={18}
                    />
                  ) : (
                    <Icon
                      icon="subway:eye"
                      className="text-gray-400 placeholderLoader rounded-lg"
                      height={18}
                    />
                  )}
                </button>
              </div>

              {/* Error Message */}
              {formik.touched?.password && formik.errors?.password && (
                <Error
                  className="text-left md:w-[400px] mx-auto mt-1 text-red-500"
                  text={formik.errors.password}
                />
              )}
            </div>
          </>
        ) : (
          <>
            <Input
              className="md:w-[400px]"
              type="number"
              placeholder="OTP"
              {...formik.getFieldProps("otp")}
            />
            {formik.touched?.otp && formik.errors?.otp && (
              <Error
                className="text-left md:w-[400px] mx-auto"
                text={formik.errors.otp}
              />
            )}
          </>
        )}
        <div className="mt-1">
          {isPassword && (
            <HStack className="justify-between">
              {props.isOTP ? (
                <span className="text-xs text-black">
                  Sign in via{" "}
                  <span
                    className="text-primary underline cursor-pointer font-semibold"
                    onClick={() => {
                      setIsPassword(false);
                      handleResend();
                      formik.setFieldValue("isPassword", false);
                    }}
                  >
                    OTP
                  </span>
                </span>
              ) : (
                <div></div>
              )}

              <button
                type="button"
                onClick={() => {
                  // handleClickForTracking(
                  //   AnalyticsConstant.forgot_password_button
                  // );
                  handleForgotPassword();
                }}
                className="underline text-xs text-secondary font-semibold"
                disabled={isForgotPasswordLoading}
              >
                {!isForgotPasswordLoading && (
                  <span className="indicator-label fw-bold">
                    Forgot your password?
                  </span>
                )}
                {isForgotPasswordLoading && (
                  <span style={{ display: "block" }}>
                    <LoaderText
                      buttonSize={ButtonSize.SMALL}
                      text={"Generating OTP"}
                    />
                  </span>
                )}
              </button>
            </HStack>
          )}
          <HStack className="justify-between">
            {!isPassword && props.isOTP && !props.isPassword && (
              <>
                <div></div>
                {canResend ? (
                  <button
                    type="button"
                    onClick={handleResend}
                    className="underline text-xs text-secondary font-semibold"
                  >
                    Resend OTP
                  </button>
                ) : (
                  <span className="text-xs">
                    Resend OTP in{" "}
                    <span className={`text-primary font-semibold`}>
                      (0:{timeLeft.toString().padStart(2, "0")})
                    </span>
                  </span>
                )}
              </>
            )}
          </HStack>
          {!isPassword && props.isPassword && (
            <HStack className="justify-between">
              <span className="text-xs text-black">
                Sign in via{" "}
                <span
                  className="text-primary underline cursor-pointer font-semibold"
                  onClick={() => {
                    setIsPassword(true);
                    formik.setFieldValue("isPassword", true);
                  }}
                >
                  Password
                </span>
              </span>

              {canResend ? (
                <button
                  type="button"
                  onClick={handleResend}
                  className="underline text-xs text-secondary font-semibold"
                >
                  Resend OTP
                </button>
              ) : (
                <span className="text-xs">
                  Resend OTP in{" "}
                  <span className={`text-primary font-semibold`}>
                    (0:{timeLeft.toString().padStart(2, "0")})
                  </span>
                </span>
              )}
            </HStack>
          )}
        </div>
      </div>

      {!isPasskeyOnly && <div className="mt-10">
        {props.isMarketplace ? (
          <SecondaryCTAButton
            event-name="Sign in"
            className={`${props.isMarketplace ? "w-full mt-4" : "w-32 mt-6"}  text-center px-auto`}
            buttonSize={ButtonSize.MEDIUM}
            loading={loading}
            type="button"
            onClick={() => {
              if (
                props.currentUserType !== undefined &&
                props.currentUserType?.hasActiveSession === true &&
                props.currentUserType?.allowMultiAccess === false
              ) {
                setShowWarning(true);
              } else {
                formik.handleSubmit();
              }
            }}
          >
            <p className="w-full text-sm leading-0"> Sign in</p>
          </SecondaryCTAButton>
        ) : (
          <PrimaryCTAButton
            event-name="Sign in"
            className="w-32 text-center px-auto "
            buttonSize={ButtonSize.MEDIUM}
            loading={loading}
            type="button"
            onClick={() => {
              if (
                props.currentUserType !== undefined &&
                props.currentUserType?.hasActiveSession === true &&
                props.currentUserType?.allowMultiAccess === false
              ) {
                setShowWarning(true);
              } else {
                formik.handleSubmit();
              }
            }}
          >
            <p className="w-full text-sm leading-0"> Sign in</p>
          </PrimaryCTAButton>
        )}
      </div>}
      {isPasskeyFeatureEnabled &&
        isPasskeySupported &&
        props.authStrategies?.includes(AuthStrategyType.PASSKEY) &&
        props.currentUserType?.hasPasskeyRegistered && (
          <>
            <div className="flex items-center justify-center my-4">
              <div className="flex-grow border-t border-gray-300"></div>
              <span className="mx-4 text-gray-500 text-xs">or</span>
              <div className="flex-grow border-t border-gray-300"></div>
            </div>
            {isMarketplace ? ( 
              <TransparentCTAButton
                className="w-full text-center mt-1 mb-4"
                buttonSize={ButtonSize.MEDIUM}
                loading={passkeyLoading}
                type="button"
                onClick={handlePasskeyLogin}
              >
                <p className="w-full text-sm">Login with Passkey</p>
              </TransparentCTAButton>
            ) : (
              <SecondaryCTAButton
                className="w-full md:w-[400px] text-center"
                buttonSize={ButtonSize.MEDIUM}
                loading={passkeyLoading}
                type="button"
                onClick={handlePasskeyLogin}
              >
                <p className="w-full text-sm">Login with Passkey</p>
              </SecondaryCTAButton>
            )}
          </>
        )}
      {showWarning && (
        <WarningModal
          open={showWarning}
          onClose={() => setShowWarning(false)}
          onSubmit={() => formik.handleSubmit()}
          isLoading={props.isLoading}
        />
      )}
    </form>
  );
};

export function WarningModal({
  open,
  onClose,
  onSubmit,
  isLoading,
}: {
  open: boolean;
  onClose: Function;
  onSubmit: Function;
  isLoading: boolean;
}) {
  return (
    <Dialog as="div" className="relative z-50" open={open} onClose={() => {}}>
      <div
        className="fixed inset-0 bg-black bg-opacity-30"
        aria-hidden="true"
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="bg-white rounded-lg shadow-xl w-[550px] p-8">
          <VStack className="space-y-4">
            <DialogTitle className="text-danger font-bold text-lg">
              <HStack className="flex items-center justify-start gap-2">
                <div className=" flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 ">
                  <TriangleAlert className="h-7 w-7 text-red-500" />
                </div>
                <p className="font-semibold text-lg ">
                  Session Active in Another Tab
                </p>
              </HStack>
            </DialogTitle>
            <div className="border-b border-gray-300"></div>
            <Description>
              <p className="text-sm text-gray-800">
                You've already logged into your account in another browser tab.
              </p>
              <p className="text-sm text-gray-800">
                Please continue using that tab, or log out there to access the
                product here.
              </p>
            </Description>
            <HStack className="flex items-center justify-end gap-2">
              <TransparentCTAButton
                className="w-20 ml-6 z-20 flex justify-start text-center rounded-md px-auto text-primary border-primary py-2 mt-2 hover:border-primary"
                buttonSize={ButtonSize.SMALL}
                type="button"
                onClick={() => {
                  onClose();
                }}
              >
                <span className="w-full text-xs leading-0">{"Cancel"}</span>
              </TransparentCTAButton>

              <PrimaryCTAButton
                className="w-auto text-center px-auto h-9 mt-2"
                buttonSize={ButtonSize.SMALL}
                type="submit"
                loading={isLoading}
                onClick={() => {
                  onSubmit();
                }}
              >
                <p className="w-full text-sm px-2 leading-0">{"Continue"}</p>
              </PrimaryCTAButton>
            </HStack>
          </VStack>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
